/** Shared frame for every authenticated page in the SPA.
 *
 * Renders a left sidebar (AppSidebar) and a main content column.
 * Two-column grid on desktop; on mobile the sidebar collapses into a
 * `<details>` strip above the content.
 *
 * Pages get a `title` + optional `description` for the standard page
 * header; the actual page-specific content goes in `children`. The
 * optional `headerAction` slot is the right-aligned button (e.g.
 * "Invite member", "New session"). Per-card permission-gating stays
 * the responsibility of the page — the sidebar gates VISIBILITY of
 * the links, but a user landing here via a typed URL still hits the
 * per-card `PermissionGate` checks.
 *
 * The live SessionPage (the video-call canvas) does NOT use this
 * layout — it's full-bleed by design. Everything else after login
 * mounts inside AppLayout.
 */
import { type ReactNode } from "react";

import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import { CallAlertBanner } from "@/modules/sessions/CallAlertBanner";


interface Props {
  title: string;
  description?: string;
  /** Optional small uppercase mint label above the title (matches the
   * marketing-site `.eyebrow` pattern). When omitted, the header
   * just shows title + description. */
  eyebrow?: string;
  children: ReactNode;
  /** Optional element rendered on the right side of the page header
   * (e.g. an "Invite member" or "New session" button). */
  headerAction?: ReactNode;
  /** Override the default `max-w-4xl` content cap if a page needs a
   * wider canvas (tables, audit log, recording list). */
  contentMaxWidth?: string;
}


export function AppLayout({
  title,
  description,
  eyebrow,
  children,
  headerAction,
  // Defaults to filling the shell. Width is decided in ONE place — the
  // container above — so every page is the same width rather than each
  // picking its own and the app looking arbitrary from screen to screen.
  contentMaxWidth = "max-w-none",
}: Props) {
  return (
    <div className="min-h-dvh">
      <TopBar />
      {/* Widened 2026-09-24 on request — "too much space on the left and
          right". This caps the sidebar AND the content, so it is the one
          knob that decides how much of a wide screen the app uses. It was
          `max-w-7xl` (1280px), which left ~350px of margin each side on a
          2000px display.

          Still capped rather than full-bleed: a table stretched across an
          ultrawide monitor is worse than one with margins. Pages that hold
          prose or forms set their own narrower `contentMaxWidth` and are
          unaffected by this. */}
      <div className="mx-auto w-full max-w-screen-2xl px-6 py-8 2xl:max-w-[105rem] lg:grid lg:grid-cols-[14rem_1fr] lg:gap-10">
        <AppSidebar />
        <main className={`min-w-0 ${contentMaxWidth} space-y-6`}>
          <header className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-2">
              {eyebrow && <div className="eyebrow">{eyebrow}</div>}
              <h1 className="page-title">{title}</h1>
              {description && (
                <p className="section-lead text-sm sm:text-base">{description}</p>
              )}
            </div>
            {headerAction && <div className="shrink-0">{headerAction}</div>}
          </header>
          {/* Above the page content on EVERY screen, because the screens
              an operator is on while working are exactly the ones that
              used to have no way of telling them somebody had joined
              their call. Renders nothing when there is nothing to say. */}
          <CallAlertBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
